import { Accordion, Typography } from '@pwskills/rachnaui';
import { classNames } from '@pwskills/rachnaui/utils';
import React, { useEffect, useState } from 'react';
import { ICurriculum, ICourseDescription } from '@/interfaces/courseDescription';
import ChevronForward from '@/assets/icons/courseDescriptionPage/ChevronForward';
import { useRouter } from 'next/router';
import { sendSkChipsetsInteractedEvent } from '@/utils/GAEventFunction';
import Lectures from './Lectures';

type Props = {
  parents: ICurriculum[] | undefined;
  categories: ICurriculum[] | undefined;
  modules: ICurriculum[] | undefined;
  lectures: ICurriculum[] | undefined;
  activeTab: string;
  courseDetails?: ICourseDescription | null;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
};
const Tabs = (props: Props) => {
  const { categories, lectures, activeTab, setActiveTab, modules, parents, courseDetails } = props;
  const [selectedModule, setSelectedModule] = useState<ICurriculum[] | undefined>(lectures);
  useEffect(() => {
    setSelectedModule(lectures);
  }, [lectures]);
  const handleTabClick = (_id: string, title?: string, position?: number) => {
    sendSkChipsetsInteractedEvent({
      chipset_type: 'curriculum_tab',
      chipset_name: title,
      chipset_id: _id,
      chipset_position: position,
      course_batch_name: courseDetails?.title || courseDetails?.name,
      course_batch_slug: courseDetails?.slug,
      course_batch_category:
        courseDetails?.categoryDetails?.title || courseDetails?.categoryDetails?.name,
      course_batch_id: courseDetails?._id,
      course_batch_cohort_id: courseDetails?.categoryDetails?._id,
      price: courseDetails?.pricing?.IN,
    });
    setActiveTab(_id);
  };
  const router = useRouter();
  const isDashboard = router.pathname.includes('/dashboard');
  const renderTitle = () => {
    return categories?.map(({ _id, title }, index) => {
      return (
        <button
          type="button"
          key={_id}
          className={classNames(
            (activeTab === _id && 'bg-orange-500 text-white') ||
              'bg-white  border border-[#EFEFEF] ',
            activeTab !== _id ? 'hover:bg-[#F8F8F8]' : '',
            `${
              isDashboard ? 'py-2 px-4 w-auto' : 'px-11 py-3'
            } rounded-[4px]  flex items-center justify-center  whitespace-nowrap`
          )}
          onClick={() => handleTabClick(_id, title, index + 1)}
          aria-hidden="true"
        >
          <Typography
            component={isDashboard ? 'body-small' : 'subheading'}
            variant="semi-bold"
            className={`${isDashboard ? 'text-gray-850' : 'text-center !text-[16px]'}  ${
              activeTab === _id && 'text-white'
            }`}
          >
            {title}
          </Typography>
        </button>
      );
    });
  };
  const renderLectures = (lecturesContent: ICurriculum[] | undefined) => {
    return lecturesContent?.map(({ _id, title, preview }, index) => {
      return (
        <Lectures
          key={_id}
          title={title}
          preview={preview}
          lectureNumber={index + 1}
          courseDetails={courseDetails}
        />
      );
    });
  };
  const renderModuleTitle = (Title: string, moduleNumber: number, isCurrentModule?: boolean) => {
    return (
      <div className="flex flex-col gap-1">
        <Typography
          component="body-tiny"
          variant="semi-bold"
          className={`${isCurrentModule ? '!text-[#E97862]' : 'text-[#757575]'}`}
        >
          {`Module${moduleNumber}`}
        </Typography>
        <Typography
          component={isDashboard ? 'body-small' : 'subheading'}
          variant="bold"
          className={` ${isCurrentModule ? '!text-[#E97862]' : 'text-[#757575]'}`}
        >
          {Title}
        </Typography>
      </div>
    );
  };
  const renderModuleContent = () => {
    let moduleNumber = 0;
    return modules?.map(({ _id, title }) => {
      moduleNumber += 1;
      const selectedLectures = parents?.filter((parentId) => {
        return parentId?.parent === _id;
      });

      const currentModule = selectedModule?.[0]?.parent === _id;
      return (
        <div
          className={`${
            selectedModule?.[0]?.parent === selectedLectures?.[0]?.parent
              ? 'bg-white'
              : 'bg-stroke-100'
          } hover:bg-white cursor-pointer flex items-center relative bg-white ${
            moduleNumber > 1 && 'border-t text-stroke-400'
          }`}
          onClick={() => {
            setSelectedModule(selectedLectures);
          }}
          aria-hidden="true"
          key={_id}
        >
          {currentModule && (
            <div
              className={`${isDashboard ? 'h-[60px]' : 'h-[78px]'} absolute w-[2px] bg-orange-500 `}
            />
          )}
          <div
            className={
              isDashboard
                ? 'flex items-center py-2 px-3  w-full justify-between'
                : 'flex items-center pl-[30px] pr-11 py-4 w-full justify-between'
            }
          >
            {renderModuleTitle(
              title,
              moduleNumber,
              currentModule // check if its current module
            )}
            <ChevronForward color={currentModule ? '#E97862' : '#3D3D3D'} />
          </div>
        </div>
      );
    });
  };
  const renderModuleContentMobileView = () => {
    let moduleNumber = 0;
    return modules?.map(({ _id, title }) => {
      const selectedLectures = parents?.filter((parentId) => {
        return parentId?.parent === _id;
      });
      moduleNumber += 1;
      const currentModule = selectedModule?.[0]?.parent === _id;
      return (
        <Accordion
          title={
            <div className={isDashboard ? '' : 'pb-5'}>
              {renderModuleTitle(title, moduleNumber, currentModule)}
            </div>
          }
          wrapperClass="bg-white m-0 !p-5 "
          className="!px-0 !py-0"
          key={_id}
          onClick={() => {
            setSelectedModule(selectedLectures);
          }}
        >
          {renderLectures(selectedLectures)}
        </Accordion>
      );
    });
  };
  return (
    <div className="flex flex-col items-center w-full gap-6 medium:gap-8">
      <div className="w-[100%]">
        <div
          className={
            isDashboard
              ? 'flex gap-2 w-full large:!max-w-[calc(100vw-320px)] min-[1300px]:!max-w-[calc(100vw-345px)] min-[1400px]:!max-w-[calc(100vw-350px)] min-[1500px]:px-auto overflow-auto hide-scrollbar'
              : 'flex  gap-4 medium:!gap-3 large:!gap-5 cursor-pointer overflow-scroll scrollbar-hide '
          }
        >
          {renderTitle()}
        </div>
      </div>
      <div className="bg-white w-[100%]">
        <div className="flex large:!grid large:!grid-cols-2 ">
          <div
            className={`flex-col max-h-[534px] scroll hidden medium:!flex medium:!min-w-[40%] large:w-auto ${
              isDashboard ? 'p-3' : ''
            }`}
          >
            {renderModuleContent()}
          </div>
          <div
            className={`${isDashboard ? '' : 'p-4'} hidden medium:!block bg-white medium:!w-full `}
          >
            <div className={isDashboard ? 'medium:!w-full' : 'rounded border medium:!w-full'}>
              <div className={`${isDashboard ? 'p-3' : ''} max-h-[531px] scroll scrollbar-hide`}>
                {renderLectures(selectedModule)}
              </div>
            </div>
          </div>
        </div>
        <div className="medium:hidden max-h-[535px] rounded scroll ">
          {renderModuleContentMobileView()}
        </div>
      </div>
    </div>
  );
};
export default Tabs;
